"use client";

import * as React from "react";
import Link from "next/link";
import clsx from "clsx";

export type BreadcrumbItem = {
    label?: string;
    href?: string | null;
    home?: boolean;
};

type Props = {
    items: BreadcrumbItem[];
    className?: string;
    itemClassName?: string;
    separator?: string;
    truncateLastOnMobile?: boolean;
};

const defaultSeparator = "›";

const HomeIcon = ({ className }: { className?: string }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        aria-label="Strona główna"
    >
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
);

export default function Breadcrumbs({
    items,
    className,
    itemClassName,
    separator = defaultSeparator,
    truncateLastOnMobile = false,
}: Props) {
    if (!items.length) return null;

    return (
        <nav aria-label="Breadcrumb" className={clsx(truncateLastOnMobile && "min-w-0 w-full max-w-full overflow-hidden", className)}>
            <ol className={clsx(
                "flex flex-nowrap items-center gap-x-1.5 text-[11px] uppercase tracking-[0.08em] whitespace-nowrap",
                truncateLastOnMobile && "min-w-0"
            )}>
                {items.map((item, i) => {
                    const isLast = i === items.length - 1;
                    const isLink = !isLast && (item.href || item.home);

                    return (
                        <li
                            key={i}
                            className={clsx(
                                "inline-flex items-center gap-x-2",
                                !(truncateLastOnMobile && isLast) && "shrink-0",
                                truncateLastOnMobile && isLast && "min-w-0 flex-1 md:flex-initial md:shrink-0",
                                itemClassName
                            )}
                        >
                            {i > 0 && (
                                <span
                                    aria-hidden
                                    className="text-neutral-400 select-none shrink-0"
                                >
                                    {separator}
                                </span>
                            )}
                            {item.home ? (
                                <Link
                                    href="/"
                                    className="transition-colors hover:opacity-70 flex items-center shrink-0"
                                    aria-label="Strona główna"
                                >
                                    <HomeIcon />
                                </Link>
                            ) : isLink ? (
                                <Link
                                    href={item.href!}
                                    className="transition-colors hover:underline underline-offset-4 shrink-0"
                                >
                                    {item.label}
                                </Link>
                            ) : (
                                <span
                                    aria-current="page"
                                    className={clsx(
                                        "text-inherit",
                                        truncateLastOnMobile && "block min-w-0 overflow-hidden text-ellipsis whitespace-nowrap md:overflow-visible md:whitespace-normal md:text-clip"
                                    )}
                                    title={truncateLastOnMobile ? String(item.label ?? "") : undefined}
                                >
                                    {item.label}
                                </span>
                            )}
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
}
